import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, ExternalLink } from "lucide-react";
import client, { API } from "@/lib/api";
import { notifyError, notifyOk, locRu } from "./_ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function ActCreateDialog({ open, onOpenChange, onCreated }) {
  const [brands, setBrands] = useState([]);
  const [cards, setCards] = useState([]);
  const [groups, setGroups] = useState([]);
  const [options, setOptions] = useState([]);

  const [brandId, setBrandId] = useState("");
  const [cardId, setCardId] = useState("auto");
  const [lang, setLang] = useState("de");
  const [date, setDate] = useState("");
  const [equipment, setEquipment] = useState({}); // id -> bool
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setBrandId(""); setCardId("auto"); setLang("de"); setDate(""); setEquipment({}); setResult(null);
    Promise.all([
      client.get("/admin/brands"),
      client.get("/admin/cards"),
      client.get("/admin/option-groups"),
      client.get("/admin/options"),
    ])
      .then(([b, c, g, o]) => {
        setBrands(b.data.filter((x) => x.active !== false));
        setCards(c.data);
        setGroups(g.data.filter((x) => x.active !== false).sort((a, z) => a.order - z.order));
        setOptions(o.data.filter((x) => x.active !== false));
      })
      .catch(notifyError);
  }, [open]);

  const freeCards = useMemo(
    () => cards.filter((c) => c.brand_id === brandId && c.status === "free").sort((a, b) => a.order - b.order),
    [cards, brandId]
  );

  const selectedCard = useMemo(() => {
    if (cardId === "auto") return freeCards[0] || null;
    return freeCards.find((c) => c.id === cardId) || null;
  }, [cardId, freeCards]);

  const toggle = (id) => setEquipment((e) => ({ ...e, [id]: !e[id] }));
  const groupOptions = (gid) => options.filter((o) => o.group_id === gid && !o.always_on).sort((a, b) => a.order - b.order);
  const setGroupAll = (gid, val) => {
    const next = { ...equipment };
    groupOptions(gid).forEach((o) => { next[o.id] = val; });
    setEquipment(next);
  };

  const chosenCount = Object.values(equipment).filter(Boolean).length;

  const submit = async () => {
    if (!brandId) return notifyError({ message: "Выберите марку" });
    if (!selectedCard) return notifyError({ message: "Нет свободной карточки для этой марки" });
    if (!date.trim()) return notifyError({ message: "Укажите дату осмотра" });
    setSaving(true);
    try {
      const body = {
        brand_id: brandId,
        vehicle_card_id: cardId === "auto" ? null : cardId,
        inspection_date: date.trim(),
        lang,
        equipment: Object.keys(equipment).filter((k) => equipment[k]),
        comment: comment.trim(),
      };
      const { data } = await client.post("/admin/acts", body);
      setResult(data);
      notifyOk(`Акт ${data.act_number} создан`);
      onCreated && onCreated();
    } catch (e) { notifyError(e); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-h-[88vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Создать акт вручную</DialogTitle>
          <DialogDescription>Выберите марку и карточку, отметьте комплектацию — акт и PDF сформируются автоматически.</DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="py-4 text-center space-y-4" data-testid="act-create-success">
            <CheckCircle2 className="w-12 h-12 mx-auto" style={{ color: "var(--ci-accent)" }} />
            <div>
              <p className="text-lg font-semibold">Акт {result.act_number} создан</p>
              <p className="text-sm text-neutral-500 mt-1">VIN: {result.vin || "—"} · Код: {result.token}</p>
            </div>
            <div className="flex gap-2 justify-center">
              <Button asChild variant="outline"><a href={`/act/${result.token}`} target="_blank" rel="noreferrer" data-testid="act-create-view"><ExternalLink className="w-4 h-4 mr-1.5" /> Открыть акт</a></Button>
              <Button asChild style={{ background: "var(--ci-accent)", color: "#1a1a1a" }}><a href={`${API}/act/${result.token}/pdf`} target="_blank" rel="noreferrer" data-testid="act-create-pdf">Скачать PDF</a></Button>
            </div>
            <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="act-create-close">Закрыть</Button>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Язык акта (PDF)</Label>
                  <Select value={lang} onValueChange={setLang}>
                    <SelectTrigger data-testid="act-lang-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="de">Deutsch</SelectItem>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="pl">Polski</SelectItem>
                      <SelectItem value="ru">Русский</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Дата осмотра</Label>
                  <Input data-testid="act-date-input" value={date} onChange={(e) => setDate(e.target.value)} placeholder="напр. 20.06.2026" className="border-neutral-300" />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>Состояние автомобиля (комментарий)</Label>
                <textarea
                  data-testid="act-comment-input"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={3}
                  placeholder="Комментарий на языке акта — необязательно"
                  className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-neutral-300"
                />
              </div>

              <div className="space-y-1.5">
                <Label>Марка</Label>
                <Select value={brandId} onValueChange={(v) => { setBrandId(v); setCardId("auto"); }}>
                  <SelectTrigger data-testid="act-brand-select"><SelectValue placeholder="Выберите марку" /></SelectTrigger>
                  <SelectContent>
                    {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {brandId && (
                <div className="space-y-1.5">
                  <Label>Карточка авто ({freeCards.length} свободных)</Label>
                  <Select value={cardId} onValueChange={setCardId}>
                    <SelectTrigger data-testid="act-card-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Следующая свободная (авто)</SelectItem>
                      {freeCards.map((c) => (
                        <SelectItem key={c.id} value={c.id}>#{c.order} · {c.vin}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedCard ? (
                    <div className="mt-2 rounded-md border border-neutral-200 bg-neutral-50 p-3 text-xs" data-testid="act-card-preview">
                      <div className="font-mono mb-1">{selectedCard.vin}</div>
                      {(selectedCard.specs || []).map((s, i) => (
                        <div key={i} className="flex justify-between py-0.5">
                          <span className="text-neutral-500">{s.label}</span>
                          <span className="font-medium">{s.value}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-red-600 mt-1">Нет свободных карточек для этой марки.</p>
                  )}
                </div>
              )}

              {brandId && (
                <div className="space-y-3">
                  <Label>Комплектация — отметьте, что есть ({chosenCount} выбрано)</Label>
                  <div className="border border-neutral-200 rounded-md divide-y divide-neutral-100 max-h-72 overflow-y-auto">
                    {groups.filter((g) => groupOptions(g.id).length > 0).map((g) => (
                      <div key={g.id} className="p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-semibold">{g.title}</span>
                          <div className="flex gap-2">
                            <button type="button" onClick={() => setGroupAll(g.id, true)} className="text-xs text-neutral-500 hover:text-black" data-testid={`act-group-all-${g.id}`}>всё есть</button>
                            <button type="button" onClick={() => setGroupAll(g.id, false)} className="text-xs text-neutral-500 hover:text-black" data-testid={`act-group-none-${g.id}`}>всё нет</button>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
                          {groupOptions(g.id).map((o) => (
                            <label key={o.id} className="flex items-start gap-2 text-sm cursor-pointer">
                              <Checkbox
                                data-testid={`act-option-${o.id}`}
                                checked={!!equipment[o.id]}
                                onCheckedChange={() => toggle(o.id)}
                                className="mt-0.5"
                              />
                              <span>{locRu(o.label)}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Отмена</Button>
              <Button data-testid="act-create-submit" onClick={submit} disabled={saving || !selectedCard} style={{ background: "var(--ci-accent)", color: "#1a1a1a" }}>
                {saving ? "Создание…" : "Сформировать акт"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
